/**
 * Benefit flip cards.
 *
 * The 3D flip itself is CSS. This only toggles a class, keeps the two toggle
 * buttons' aria-expanded in sync, and makes the hidden face unreachable.
 *
 * One delegated listener per section, so any number of cards works and no
 * listener is attached per card. Scoped to <sg-flip-benefits>, so several
 * instances coexist and a Theme Editor section reload re-initialises cleanly.
 */
class SgFlipBenefits extends HTMLElement {
  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('[data-sgf-card]'));
    if (!this.cards.length) return;

    this.onClick = (event) => {
      // A link on the back must navigate, not flip the card back over.
      if (event.target.closest('a')) return;

      const card = event.target.closest('[data-sgf-card]');
      if (!card || !this.contains(card)) return;

      // The toggle buttons are real buttons, so keyboard activation arrives
      // here as a click — no separate keydown handling needed.
      this.#setFlipped(card, !card.classList.contains('is-flipped'));
    };
    this.addEventListener('click', this.onClick);

    // Start with every back face inert, so tabbing cannot land on text that
    // is rotated away and invisible.
    this.cards.forEach((card) => this.#setFlipped(card, false, true));
  }

  #setFlipped(card, flipped, silent = false) {
    card.classList.toggle('is-flipped', flipped);

    const front = card.querySelector('[data-sgf-front]');
    const back = card.querySelector('[data-sgf-back]');

    // Hide whichever face is turned away from both the accessibility tree and
    // the tab order. `inert` does not affect layout, so the card's height —
    // which comes from the taller face — is unchanged.
    this.#setHidden(front, flipped);
    this.#setHidden(back, !flipped);

    card.querySelectorAll('[data-sgf-toggle]').forEach((btn) => {
      btn.setAttribute('aria-expanded', String(flipped));
    });

    // Move focus to the face now showing, but only in response to a real
    // interaction — never during the initial pass.
    if (!silent) {
      const target = (flipped ? back : front)?.querySelector('[data-sgf-toggle]');
      target?.focus({ preventScroll: true });
    }
  }

  #setHidden(face, hidden) {
    if (!face) return;
    face.toggleAttribute('inert', hidden);
    face.setAttribute('aria-hidden', String(hidden));
  }

  disconnectedCallback() {
    if (this.onClick) this.removeEventListener('click', this.onClick);
  }
}

if (!customElements.get('sg-flip-benefits')) {
  customElements.define('sg-flip-benefits', SgFlipBenefits);
}
